import { gql } from "@apollo/client"
import { countryCodeToName } from "@oysterhr/common"
import { useTranslation } from "react-i18next"
import type { AdvisorKnowledgeBase } from "../../types"
import { useSubdivisionName } from "@oysterhr/feature-components"

export type Module = AdvisorKnowledgeBase<{
  name: string
  isoAlpha2Code: string
  workingHourRules: Array<{
    employmentType: string
    workingWeek: {
      maxDuration: number
      standardDuration: number
    }
  }>
}>

export const workScheduleFragment: Module["queryFragment"] = gql`
  fragment kbWorkSchedule on CountryIntelligence {
    name
    isoAlpha2Code
    workingHourRules {
      employmentType
      workingWeek {
        minDuration
        maxDuration
        standardDuration
      }
    }
  }
`

export const TranslationComponent: Module["TranslationComponent"] = ({ data, input }) => {
  const [t] = useTranslation("knowledge_base/HiringAdvisor")
  const { isoAlpha2Code, workingHourRules } = data

  const subdivisionCode = input?.subdivisionCode
  const subdivisionName = useSubdivisionName(isoAlpha2Code || "", subdivisionCode || "")
  if (!workingHourRules?.length) {
    throw new Error(
      `No working hour rules found (country: ${isoAlpha2Code} subdivision: ${subdivisionCode})`,
    )
  }
  if (workingHourRules && workingHourRules.length > 0) {
    const countryName = countryCodeToName(isoAlpha2Code)

    return (
      <div>
        <ul className="list-inside list-disc">
          <span>
            {t("workSchedule.fullTimeDescription", {
              countryName: [countryName, subdivisionName].filter(Boolean).join(", "),
            })}
          </span>
          {workingHourRules.map(({ employmentType, workingWeek }) => {
            const { standardDuration } = workingWeek

            return (
              <li className="pl-4" key={employmentType}>
                {t("workSchedule.item", {
                  standardDuration: standardDuration,
                  position: t("workSchedule.positions." + employmentType),
                })}
              </li>
            )
          })}
        </ul>
        <span>{t("workSchedule.partTimeDescription")}</span>
      </div>
    )
  } else {
    return null
  }
}

export const checkDataPresence: Module["checkDataPresence"] = (data): boolean => {
  return data?.workingHourRules?.length > 0
}
